import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router'
import { lifespan } from '../../shared/tree'
import { Choice } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, type Me } from '@/lib/api'
import { firstName } from '@/lib/tree'
import { PageLoader } from '@/components/Loader'

type Invite = {
  family: string
  kind: 'family' | 'casual'
  from: string
  person: { id: string; name: string; born: string | null; portrait: string | null } | null
  home: string | null
}

// Where an invite link lands, once the person has signed in with the email it was sent to (or an invite opened from
// the ones waiting for them on Home, by its id). If the invite names
// someone on the tree it asks "Is this you?"; saying yes hands them that person's page. Without a home to join,
// they name their own.
export function Join({ me }: { me: Me }) {
  const { token = '' } = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [isMe, setIsMe] = useState<'yes' | 'no'>('yes')
  const [home, setHome] = useState(`${firstName(me.name)}’s home`)
  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<Invite>(`/invites/${encodeURIComponent(token)}`),
    retry: false,
  })
  const accept = useMutation({
    mutationFn: () =>
      api<{ familyId: string }>(`/invites/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        body: {
          isMe: !!invite.data?.person && isMe === 'yes',
          home: invite.data?.home || invite.data?.kind !== 'family' ? undefined : home.trim(),
        },
      }),
    onSuccess: async ({ familyId }) => {
      await queryClient.invalidateQueries({ queryKey: ['families'] })
      await queryClient.invalidateQueries({ queryKey: ['circles'] })
      await queryClient.invalidateQueries({ queryKey: ['invites'] })
      // Everyone is offered from your first circle.
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      navigate(`/circle/${familyId}`, { replace: true })
    },
  })

  const decline = useMutation({
    mutationFn: () => api(`/invites/${encodeURIComponent(token)}/decline`, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invites'] })
      navigate('/', { replace: true })
    },
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    accept.mutate()
  }

  if (invite.isPending) return <PageLoader />
  if (invite.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <h1 className="text-xl">This invite can’t be used</h1>
          </CardTitle>
          <CardDescription>
            {invite.error.message} You’re signed in as {me.email}.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }
  const { person } = invite.data
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h1 className="text-xl">Join {invite.data.family}</h1>
        </CardTitle>
        <CardDescription>{invite.data.from} invited you.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-6">
          {person && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Avatar person={person} className="size-14 text-xl" />
                <span className="flex flex-col">
                  <span className="text-lg font-semibold tracking-tight">{person.name}</span>
                  {person.born && (
                    <span className="text-sub text-muted-foreground">
                      {lifespan({ living: true, born: person.born, died: null })}
                    </span>
                  )}
                </span>
              </div>
              <Choice
                legend="Is this you?"
                hint={`If it is, ${person.name}’s page in the family tree becomes yours to keep.`}
                value={isMe}
                options={[
                  ['yes', 'Yes, that’s me'],
                  ['no', 'No'],
                ]}
                onChange={(v) => setIsMe(v as 'yes' | 'no')}
              />
            </div>
          )}
          {invite.data.kind !== 'family' ? (
            <p className="text-muted-foreground">
              No family tree here and no homes to set up: just the people and what you share.
            </p>
          ) : invite.data.home ? (
            <p className="text-muted-foreground">You’ll join {invite.data.home}.</p>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="home">What should we call your home?</Label>
              <Input id="home" required maxLength={80} value={home} onChange={(e) => setHome(e.target.value)} />
              <p className="text-sub text-muted-foreground">
                Your household inside the circle: you and the people you live with.
              </p>
            </div>
          )}
          <FormError error={accept.error ?? decline.error} />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={accept.isPending || decline.isPending}>
              Join {invite.data.family}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={accept.isPending || decline.isPending}
              onClick={() => decline.mutate()}
            >
              No thanks
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
